import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  TextField,
  Divider,
} from '@mui/material';
import {
  AccessTime,
  NavigateNext,
  NavigateBefore,
  Flag,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { studentExamFlowAPI } from '../../../services/api';

const TakeExam = () => {
  const { examinationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const studentExamId = location.state?.studentExamId;
  const subjectId = location.state?.subjectId;
  const subjectName = location.state?.subjectName;

  // State
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(true);
  const [examination, setExamination] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [submission, setSubmission] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);

  const autoSaveTimerRef = useRef(null);
  const lastSavedAnswersRef = useRef({});
  const hasStartedRef = useRef(false);

  // Start exam and load data on mount
  useEffect(() => {
    if (!studentExamId) {
      toast.error('Invalid examination access');
      navigate('/student/dashboard');
      return;
    }

    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      startAndLoadExam();
    }
  }, []);

  const startAndLoadExam = async () => {
    try {
      setStarting(true);
      setLoading(true);

      // Start the subject exam (accept terms with subjectId)
      await studentExamFlowAPI.startExam(studentExamId, {
        termsAccepted: true,
        subjectId
      });

      // Load exam data
      await loadExamData();

      toast.success('Subject examination started successfully');
    } catch (error) {
      console.error('Error starting exam:', error);
      toast.error(error.response?.data?.message || 'Failed to start examination');
      navigate('/student/dashboard');
    } finally {
      setStarting(false);
    }
  };

  // Auto-save timer
  useEffect(() => {
    if (submission && !starting) {
      autoSaveTimerRef.current = setInterval(() => {
        autoSaveAnswers();
      }, 30000); // Auto-save every 30 seconds

      return () => {
        if (autoSaveTimerRef.current) {
          clearInterval(autoSaveTimerRef.current);
        }
      };
    }
  }, [submission, starting, answers]);

  // Countdown timer (subject-based)
  useEffect(() => {
    if (timeLeft !== null && !starting) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1000) {
            handleAutoSubmit();
            clearInterval(timer);
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeLeft !== null, starting]);

  // Prevent page close/refresh without warning
  useEffect(() => {
    if (submission && !starting) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = '';
        autoSaveAnswers(); // Try to save before leaving
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [submission, starting, answers]);

  const loadExamData = async () => {
    try {
      setLoading(true);
      // Pass subjectId as query parameter
      const response = await studentExamFlowAPI.getExamQuestions(examinationId, subjectId);
      const data = response.data.data;

      const questionsData = data.questions || [];
      setAllQuestions(questionsData);
      setQuestions(questionsData);
      setSubmission(data.submission);

      // Load existing answers
      const answersMap = {};
      const markedSet = new Set();

      questionsData.forEach(q => {
        if (q.studentAnswer) {
          answersMap[q._id] = q.studentAnswer.selectedOption || q.studentAnswer.answerText || '';
          if (q.studentAnswer.isMarkedForReview) {
            markedSet.add(q._id);
          }
        }
      });

      setAnswers(answersMap);
      setMarkedForReview(markedSet);
      lastSavedAnswersRef.current = { ...answersMap };

      // Calculate time left from subject-specific end time
      if (data.subjectSubmission) {
        const endTime = new Date(data.subjectSubmission.endTime);
        const now = new Date();
        const diff = endTime - now;
        if (diff <= 5000) {
          toast.error('Subject time has already expired. Returning to dashboard.');
          navigate('/student/dashboard');
          return;
        }
        setTimeLeft(diff);
        setExamination(data.submission.examination);
      }
    } catch (error) {
      console.error('Error loading exam data:', error);
      toast.error(error.response?.data?.message || 'Failed to load examination');
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const autoSaveAnswers = async () => {
    if (!submission) return;

    // Check if there are unsaved changes
    const hasChanges = Object.keys(answers).some(
      qId => answers[qId] !== lastSavedAnswersRef.current[qId]
    );

    if (!hasChanges) return;

    try {
      setAutoSaving(true);

      // Save all changed answers
      const savePromises = [];

      for (const questionId of Object.keys(answers)) {
        if (answers[questionId] !== lastSavedAnswersRef.current[questionId]) {
          const question = questions.find(q => q._id === questionId);
          const answerData = {
            submissionId: submission._id,
            questionId,
            isMarkedForReview: markedForReview.has(questionId),
          };

          if (question?.questionType === 'mcq' || question?.questionType === 'true-false') {
            answerData.selectedOption = answers[questionId];
          } else {
            answerData.answerText = answers[questionId];
          }

          savePromises.push(studentExamFlowAPI.saveAnswer(answerData));
        }
      }

      await Promise.all(savePromises);
      lastSavedAnswersRef.current = { ...answers };
    } catch (error) {
      console.error('Auto-save error:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleToggleReview = (questionId) => {
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleNavigateQuestion = (index) => {
    autoSaveAnswers(); // Save before navigating
    setCurrentQuestionIndex(index);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      handleNavigateQuestion(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      handleNavigateQuestion(currentQuestionIndex - 1);
    }
  };

  const handleAutoSubmit = async () => {
    try {
      await autoSaveAnswers();
      await studentExamFlowAPI.submitExam(submission._id, {
        isAutoSubmit: true,
        subjectId
      });
      toast.warning('Time is up! Your answers have been automatically submitted and recorded as your final submission.', { autoClose: 8000 });
      navigate('/student/dashboard');
    } catch (error) {
      console.error('Auto-submit error:', error);
      toast.error('Failed to submit examination');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await autoSaveAnswers(); // Save all pending answers
      await studentExamFlowAPI.submitExam(submission._id, {
        isAutoSubmit: false,
        subjectId
      });
      toast.success('Subject examination submitted successfully');
      navigate('/student/dashboard');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit examination');
    } finally {
      setSubmitting(false);
      setShowSubmitDialog(false);
    }
  };

  const getQuestionStatus = (questionId) => {
    if (markedForReview.has(questionId)) return 'review';
    // Check if answer exists and is not empty/null
    const answer = answers[questionId];
    if (answer && answer.trim() !== '') return 'answered';
    return 'unanswered';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered': return 'success.main';
      case 'review': return 'warning.main';
      case 'unanswered': return 'grey.400';
      default: return 'grey.400';
    }
  };

  const formatTime = (ms) => {
    if (!ms || ms < 0) return '00:00:00';
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const currentQuestion = questions[currentQuestionIndex];
  const answeredCount = Object.keys(answers).filter(qId => answers[qId]).length;
  const reviewCount = markedForReview.size;
  const unansweredCount = questions.length - answeredCount;

  // Loading/Starting State
  if (starting || loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          {starting ? 'Starting examination...' : 'Loading questions...'}
        </Typography>
      </Box>
    );
  }

  // Check if we have questions
  if (!questions || questions.length === 0) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            No Questions Available
          </Typography>
          <Typography variant="body2" paragraph>
            {subjectId
              ? `No questions found for subject: ${subjectName || 'Selected Subject'}`
              : 'No questions available for this examination'
            }
          </Typography>
          <Typography variant="body2">
            <strong>Debug Information:</strong>
          </Typography>
          <Typography variant="body2" component="div">
            • Examination ID: {examinationId}<br />
            • Subject Filter: {subjectId ? `${subjectName} (${subjectId})` : 'None'}<br />
            • Total Questions Loaded: {allQuestions?.length || 0}<br />
            • Filtered Questions: {questions?.length || 0}
          </Typography>
        </Alert>

        {allQuestions && allQuestions.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Available Subjects in This Exam:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              {[...new Set(allQuestions.map(q => `${q.subject?.name} (${q.subject?.code})`))].filter(Boolean).join(', ')}
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/student/dashboard')}
          >
            Back to Dashboard
          </Button>
          {subjectId && allQuestions && allQuestions.length > 0 && (
            <Button
              variant="outlined"
              onClick={() => {
                console.log('Showing all questions instead of filtered');
                setQuestions(allQuestions);
              }}
            >
              Show All Questions (Debug)
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // Main Examination Interface
  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', gap: 2 }}>
      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Timer Header */}
        <Paper elevation={2} sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {examination?.name}
              {subjectName && (
                <Chip
                  label={subjectName}
                  size="small"
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Question {currentQuestionIndex + 1} of {questions.length}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Time Remaining
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime color={timeLeft < 300000 ? 'error' : 'primary'} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: timeLeft < 300000 ? 'error.main' : 'primary.main'
                }}
              >
                {formatTime(timeLeft)}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Question Area */}
        <Paper elevation={2} sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
          {currentQuestion && (
            <>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Question {currentQuestionIndex + 1}
                  </Typography>
                  <Chip
                    label={`${currentQuestion.marks} ${currentQuestion.marks === 1 ? 'Mark' : 'Marks'}`}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                  {currentQuestion.questionText}
                </Typography>
              </Box>

              {(currentQuestion.questionType === 'mcq' || currentQuestion.questionType === 'true-false') && (
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={answers[currentQuestion._id] || null}
                    onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                  >
                    {currentQuestion.options?.map((option, index) => (
                      <Paper
                        key={index}
                        elevation={1}
                        sx={{
                          mb: 1,
                          p: 2,
                          border: answers[currentQuestion._id] === option.optionText ? '2px solid' : '1px solid',
                          borderColor: answers[currentQuestion._id] === option.optionText ? 'primary.main' : 'grey.300',
                          backgroundColor: answers[currentQuestion._id] === option.optionText ? 'primary.lighter' : 'transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'grey.50'
                          }
                        }}
                        onClick={() => handleAnswerChange(currentQuestion._id, option.optionText)}
                      >
                        <FormControlLabel
                          value={option.optionText}
                          control={<Radio />}
                          label={option.optionText}
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    ))}
                  </RadioGroup>
                </FormControl>
              )}

              {(currentQuestion.questionType === 'short-answer' || currentQuestion.questionType === 'essay') && (
                <TextField
                  fullWidth
                  multiline
                  rows={currentQuestion.questionType === 'essay' ? 10 : 4}
                  placeholder="Type your answer here..."
                  value={answers[currentQuestion._id] || ''}
                  onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                  variant="outlined"
                />
              )}

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={markedForReview.has(currentQuestion._id)}
                      onChange={() => handleToggleReview(currentQuestion._id)}
                      icon={<Flag />}
                      checkedIcon={<Flag />}
                      color="warning"
                    />
                  }
                  label="Mark for Review"
                />
                {autoSaving && (
                  <Chip
                    label="Saving..."
                    size="small"
                    color="info"
                    icon={<CircularProgress size={16} />}
                  />
                )}
              </Box>
            </>
          )}
        </Paper>

        {/* Navigation Buttons */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            startIcon={<NavigateBefore />}
            onClick={handlePreviousQuestion}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setShowSubmitDialog(true)}
          >
            Submit Examination
          </Button>
          <Button
            variant="outlined"
            endIcon={<NavigateNext />}
            onClick={handleNextQuestion}
            disabled={currentQuestionIndex === questions.length - 1}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Question Navigation Sidebar */}
      <Paper elevation={2} sx={{ width: 280, p: 2, overflow: 'auto' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Questions
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: 'success.main' }} />
            <Typography variant="caption">Answered ({answeredCount})</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: 'warning.main' }} />
            <Typography variant="caption">Marked for Review ({reviewCount})</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: 'grey.400' }} />
            <Typography variant="caption">Not Answered ({unansweredCount})</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={1}>
          {questions.map((q, index) => {
            const status = getQuestionStatus(q._id);
            const isActive = index === currentQuestionIndex;

            return (
              <Grid item xs={3} key={q._id}>
                <Box
                  onClick={() => handleNavigateQuestion(index)}
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    backgroundColor: getStatusColor(status),
                    color: 'white',
                    fontWeight: 600,
                    cursor: 'pointer',
                    border: isActive ? '3px solid' : 'none',
                    borderColor: isActive ? 'primary.dark' : 'transparent',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  {index + 1}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>Confirm Submission</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Warning:</strong> This action will be recorded as your final exam submission for this subject. Once submitted, you CANNOT re-enter or modify your answers.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
            Submission Summary:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2">• Total Questions: {questions.length}</Typography>
            <Typography variant="body2" color={answeredCount === questions.length ? 'success.main' : 'warning.main'}>• Answered: {answeredCount}</Typography>
            <Typography variant="body2">• Marked for Review: {reviewCount}</Typography>
            <Typography variant="body2" color={unansweredCount > 0 ? 'error.main' : 'text.secondary'}>• Not Answered: {unansweredCount}</Typography>
          </Box>
          {unansweredCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              You still have {unansweredCount} unanswered question{unansweredCount > 1 ? 's' : ''}. Are you sure you want to finish?
            </Alert>
          )}
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 700 }}>
            Are you sure you want to submit this subject examination?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)} disabled={submitting} variant="outlined">
            Go Back
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Yes, Submit Final Answers'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TakeExam;
